// Canvas drawing helpers for the Locations globe. Pure: each takes the 2D context + camera and
// draws one layer. Visual language (atmosphere gradient, faint border map, rim) matches the home
// globe; the new pieces are per-country fills and the city marker.

import { lonLatToUnit, type UPoint } from "@/lib/globe-borders";
import type { CountryFeature } from "@/lib/globe-features";
import { project, type Projected } from "./projection";
import type { Camera } from "./types";

type RGB = [number, number, number];

interface GlobePalette {
  grn: RGB; // country fills, markers, atmosphere tint
  ring: RGB; // globe rim circle
  borders: string; // faint country-border strokes
}

const DARK_PALETTE: GlobePalette = {
  grn: [91, 140, 255],
  ring: [247, 247, 248],
  borders: "rgba(190,196,214,0.11)",
};

const LIGHT_PALETTE: GlobePalette = {
  grn: [4, 120, 87],
  ring: [10, 10, 11],
  borders: "rgba(60,66,84,0.16)",
};

let palette = DARK_PALETTE;

/** Canvas colors can't follow CSS media queries — the globe component calls
 *  this when the browser's color-scheme preference changes. */
export function setGlobeTheme(prefersLight: boolean): void {
  palette = prefersLight ? LIGHT_PALETTE : DARK_PALETTE;
}

export function globeGreen(): RGB {
  return palette.grn;
}

type Ctx = CanvasRenderingContext2D;

export function drawAtmosphere(ctx: Ctx, cam: Camera): void {
  const { cx, cy, rG } = cam;
  const { grn } = palette;
  const atm = ctx.createRadialGradient(cx, cy, rG * 0.76, cx, cy, rG * 1.45);
  atm.addColorStop(0, `rgba(${grn[0]},${grn[1]},${grn[2]},0)`);
  atm.addColorStop(0.4, `rgba(${grn[0]},${grn[1]},${grn[2]},0.06)`);
  atm.addColorStop(1, "rgba(0,0,0,0)");
  ctx.beginPath();
  ctx.arc(cx, cy, rG * 1.45, 0, Math.PI * 2);
  ctx.fillStyle = atm;
  ctx.fill();
}

export function drawRim(ctx: Ctx, cam: Camera): void {
  const { ring } = palette;
  ctx.beginPath();
  ctx.arc(cam.cx, cam.cy, cam.rG, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(${ring[0]},${ring[1]},${ring[2]},0.18)`;
  ctx.lineWidth = 1;
  ctx.stroke();
}

/** Faint front-facing country borders behind the fills (same look as the home globe). */
export function drawBorders(ctx: Ctx, cam: Camera, borders: UPoint[][]): void {
  ctx.strokeStyle = palette.borders;
  ctx.lineWidth = 0.6;
  for (const poly of borders) {
    let drawing = false;
    ctx.beginPath();
    for (const pt of poly) {
      const p = project(pt, cam);
      if (p.depth > 0) {
        if (drawing) ctx.lineTo(p.x, p.y);
        else {
          ctx.moveTo(p.x, p.y);
          drawing = true;
        }
      } else {
        drawing = false;
      }
    }
    ctx.stroke();
  }
}

/** Split a projected closed ring into the runs that lie on the front hemisphere, inserting a
 *  point on the limb (depth = 0) wherever it crosses, so a fill hugs the globe edge. */
function frontSubpaths(pts: Projected[]): Projected[][] {
  const n = pts.length;
  const subs: Projected[][] = [];
  let cur: Projected[] | null = null;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    if (a.depth > 0) (cur ??= []).push(a);
    if (a.depth > 0 !== b.depth > 0) {
      const t = a.depth / (a.depth - b.depth);
      const h: Projected = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, depth: 0 };
      if (cur) {
        cur.push(h);
        subs.push(cur);
        cur = null;
      } else {
        cur = [h];
      }
    }
  }
  if (cur) subs.push(cur);
  return subs;
}

interface FillOpts {
  fill: RGB;
  fillAlpha: number;
  strokeAlpha: number;
}

/** Fill one country (all outer rings) in `fill`, front hemisphere only. */
export function fillCountry(ctx: Ctx, cam: Camera, feature: CountryFeature, opts: FillOpts): void {
  ctx.beginPath();
  let drew = false;
  for (const ring of feature.rings) {
    const projected = ring.unit.map((u) => project(u, cam));
    for (const sub of frontSubpaths(projected)) {
      if (sub.length < 2) continue;
      ctx.moveTo(sub[0].x, sub[0].y);
      for (let i = 1; i < sub.length; i++) ctx.lineTo(sub[i].x, sub[i].y);
      ctx.closePath();
      drew = true;
    }
  }
  if (!drew) return;
  const [r, g, b] = opts.fill;
  ctx.fillStyle = `rgba(${r},${g},${b},${opts.fillAlpha})`;
  ctx.fill();
  ctx.strokeStyle = `rgba(${r},${g},${b},${opts.strokeAlpha})`;
  ctx.lineWidth = 1;
  ctx.stroke();
}

interface MarkerOpts {
  glowR?: number;
  dotR?: number;
  /** Glow opacity at the center (0–1). */
  glowAlpha?: number;
}

/** A glowing green dot at a lon/lat, drawn only when on the front hemisphere. Used both for the
 *  city marker (focus pages) and the subtle availability dots on the hub (smaller). */
export function drawMarker(
  ctx: Ctx,
  cam: Camera,
  lon: number,
  lat: number,
  opts?: MarkerOpts
): void {
  const p = project(lonLatToUnit(lon, lat), cam);
  if (p.depth <= 0) return;
  const glowR = opts?.glowR ?? 16;
  const dotR = opts?.dotR ?? 3;
  const glowAlpha = opts?.glowAlpha ?? 0.6;
  const { grn } = palette;
  const gr = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowR);
  gr.addColorStop(0, `rgba(${grn[0]},${grn[1]},${grn[2]},${glowAlpha})`);
  gr.addColorStop(1, "rgba(0,0,0,0)");
  ctx.beginPath();
  ctx.arc(p.x, p.y, glowR, 0, Math.PI * 2);
  ctx.fillStyle = gr;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(p.x, p.y, dotR, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(${grn[0]},${grn[1]},${grn[2]},1)`;
  ctx.fill();
}
